import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { me, register } from '@/store/auth/api';
import { Credentials, PutUserResponse, PutUserToAnswerBody } from '@/store/auth/types';
import type { Client, globalRegions, Identity, User } from 'altru-types';
import { AltruApiResponse } from 'altru-types/@types/generic-response';
import i18n from '@/i18n';
import { InitialQueryParams } from '@/types/app';
import showErrorView from '@/utils/showErrorView';
import { NavigationFailure } from 'vue-router';

@Module({ namespaced: true })
export default class AuthVuexModule extends VuexModule {
  credentials: Credentials | null = null;
  anonymousFlow = false;
  payload: string | null = null;
  meValue: Identity | null = null;

  get isAnonymousFlowEnabled(): boolean {
    return this.anonymousFlow;
  }

  get credentialsValue(): Credentials | null {
    return this.credentials;
  }

  get payloadValue(): string | null {
    return this.payload;
  }

  get me(): Identity | null {
    return this.meValue;
  }

  @Mutation
  toggleAnonymousFlow(value = true): void {
    this.anonymousFlow = value;
  }

  @Mutation
  setCredentialsValue(value: Credentials | null): void {
    this.credentials = value;
  }

  @Mutation
  setPayloadValue(value: string): void {
    this.payload = value;
  }

  @Mutation
  setIdentity(value: Identity): void {
    this.meValue = value;
  }

  @Action({ rawError: true })
  storePayloadValue(payload: string): void {
    this.context.commit('setPayloadValue', payload);
  }

  @Action({ rawError: true })
  async registerUser(registerParams: PutUserToAnswerBody, region: globalRegions): PutUserResponse {
    try {
      const { info, answerId, payload } = registerParams;
      return await register(answerId, payload, region, info);
    } catch (e) {
      return e.response.data;
    }
  }

  @Action({ rawError: true })
  async getMe(params: {
    accessToken: string;
    client: string;
    uid: string;
    workspace: Client;
  }): Promise<User | null | void | NavigationFailure> {
    try {
      const isIframed: boolean = this.context.rootGetters['status/isIframed'];
      const iframeParentIsAdminApp: boolean = this.context.rootGetters['status/iframeParentIsAdminApp'];
      const { accessToken, client, uid, workspace } = params;
      const meValue = await me(accessToken, client, uid, workspace?.global_region ?? 'us');

      const meData = meValue.data;
      if (meData) {
        this.context.commit('setIdentity', meValue.data);

        // Check for userInfo after getting the identity to update the status of the terms accepted property
        const userInfo = meValue.data?.users.find((user) => user.client_id === workspace?.id);

        if (userInfo) {
          this.context.commit('users/setUserInfo', userInfo, { root: true });

          if (isIframed) {
            // Auto-check the T&C only if iframed for now based on user
            if (userInfo?.terms_accepted_at) {
              await this.context.dispatch('clients/toggleWorkspaceTermsAccepted', userInfo.terms_accepted_at, {
                root: true,
              });
              // Only redirect if T&C are not check if recorder is used in admin
            } else if (iframeParentIsAdminApp) {
              return showErrorView(i18n.global.t('NO TERMS SET ON ADMIN'));
            }
          }
          return userInfo;
        } else {
          return showErrorView(i18n.global.t('NO USER FOR IDENTITY'));
        }
      }
      return showErrorView(i18n.global.t('IDENTITY RETRIEVAL ISSUE'));
    } catch (e) {
      if (e.response?.data) {
        return e.response.data;
      }
      console.error(e);
      return e.response;
    }
  }

  @Action({ rawError: true })
  async initCredentialsInIframe({
    headersObject,
  }: {
    headersObject: Credentials | null;
  }): Promise<AltruApiResponse<User>> {
    this.context.commit('setCredentialsValue', headersObject);

    const questionWorkspace = this.context.rootState.clients.workspace;

    return await this.context.dispatch('getMe', {
      accessToken: this.credentials?.['access-token'],
      client: this.credentials?.client,
      uid: this.credentials?.uid,
      workspace: questionWorkspace,
    });
  }

  @Action({ rawError: true })
  async initCredentials({
    headersObject,
    queryParams,
  }: {
    headersObject: Credentials | null;
    queryParams: InitialQueryParams;
  }): Promise<void> {
    await this.context.dispatch(
      'clients/getWorkspaceWithCredentials',
      {
        clientId: queryParams.client_id,
        authHeaders: headersObject,
      },
      { root: true },
    );
    const questionWorkspace = this.context.rootState.clients.workspace;

    this.context.commit('setCredentialsValue', headersObject);
    await this.context.dispatch('getMe', {
      accessToken: this.credentials?.['access-token'],
      client: this.credentials?.client,
      uid: this.credentials?.uid,
      workspace: questionWorkspace,
    });

    /* https://jira.icims.net/browse/VIDSTUD-1337
     * If we get credentials from admin, make sure we update the "authorization" param to match the same
     * workspace the question comes from; this is to handle an edge case where a user is answering to a question from
     * workspace A, but it's logged in into workspace B while doing it.
     */
    if (this.credentials?.authorization) {
      const userLoggedInAdminExists = this.meValue?.users.find(
        (user) => user.authorization_token === this.credentials?.authorization,
      );
      if (userLoggedInAdminExists?.client_id !== questionWorkspace?.id) {
        const userOnQuestionWorkspace = this.meValue?.users.find((user) => user.client_id === questionWorkspace?.id);
        if (userOnQuestionWorkspace) {
          this.context.commit('setCredentialsValue', {
            ...this.credentialsValue,
            authorization: userOnQuestionWorkspace.authorization_token,
          });
        } else {
          /* No user exists for that identity on that workspace, we need to create a new one with employee role
           * For now, we just notice the user and let it fail.
           * TODO: improve flow */
          await this.context.dispatch(
            'ui/setErrorMessage',
            {
              value: i18n.global.t('NO USER FOUND FOR IDENTITY ON WORKSPACE {0}', [
                questionWorkspace?.name || questionWorkspace?.id,
              ]),
              variant: 'error',
              persistent: true,
            },
            { root: true },
          );
        }
      }
    }
  }
}
